import { logger } from "@/utils/logger";
import { getStoredInterestData, mergeWithStoredInterestData } from './interestDataUtils';
import { getDealerID } from './dealerDataUtils';
import { getUTMSource, getUTMCampaign, getD2CChatbotCookie } from './cookieUtils';
import { ga4Tracker } from './ga4Tracking';
interface ContactData {
  Name?: string;
  Phone?: string;
  Email?: string;
}

interface InterestData {
  summary?: string;
  message?: string; // Add message field here
  vehicle?: {
    make?: string;
    model?: string;
    version?: string;
    year?: number | string;
    color?: string;
    vin?: string;
  };
  // SECURITY: Contact information removed - no longer stored in localStorage
  department?: string;
  dealerid?: string;
  utm_source?: string;
  utm_campaign?: string;
  serviceDetails?: {
    date?: string;
    time?: string;
    email?: string;
  };
}

interface UnbounceWebhookPayload {
  // Standard Unbounce fields as separate parameters
  page_id: string;
  page_name: string;
  page_url: string;
  variant: string;
  
  // Lead data as JSON string
  data_json: string;
}

// Simplified rate limiting - reduce cache duration to prevent long delays
const leadPushCache = new Map<string, number>();
const CACHE_DURATION = 2000; // Reduced to 2 seconds

const generateLeadKey = (contactData: ContactData): string => {
  return `${contactData.Name || ''}-${contactData.Phone || ''}-${contactData.Email || ''}`;
};

export const LeadPushtoD2C = async (contactData: ContactData, interestData?: InterestData): Promise<boolean> => {
  // Generate a unique key for this lead
  const leadKey = generateLeadKey(contactData);
  const now = Date.now();
  
  // Check if we've already processed this lead recently
  const lastPushTime = leadPushCache.get(leadKey);
  if (lastPushTime && (now - lastPushTime) < CACHE_DURATION) {
    logger.log('=== DUPLICATE LEAD PUSH DETECTED - SKIPPING ===');
    logger.log('Lead key:', leadKey);
    logger.log('Last push was', now - lastPushTime, 'ms ago');
    return true; // Return true to indicate "success" since we already pushed this recently
  }
  
  // Store the current push time
  leadPushCache.set(leadKey, now);
  
  logger.log('=== LeadPushtoD2C STARTING ===');
  logger.log('Lead key:', leadKey);
  
  // Validate that we have at least phone or email
  if (!contactData.Phone && !contactData.Email) {
    logger.log('LeadPushtoD2C: No phone or email provided, skipping lead push');
    leadPushCache.delete(leadKey); // Remove from cache since we didn't actually push
    return false;
  }

  try {
    // Auto-detect UTM parameters from cookies
    const utmSourceFromCookie = getUTMSource();
    const utmCampaignFromCookie = getUTMCampaign();
    logger.log('=== UTM PARAMETERS FROM COOKIES ===');
    logger.log('UTM Source from cookie:', utmSourceFromCookie);
    logger.log('UTM Campaign from cookie:', utmCampaignFromCookie);
    
    // Merge with stored interest data to get the most complete picture
    let finalInterestData = await mergeWithStoredInterestData(interestData);
    
    // Override with UTM parameters from cookies if available (cookies take priority)
    if (utmSourceFromCookie) {
      finalInterestData = { ...finalInterestData, utm_source: utmSourceFromCookie };
    }
    if (utmCampaignFromCookie) {
      finalInterestData = { ...finalInterestData, utm_campaign: utmCampaignFromCookie };
    }
    
    logger.log('=== FINAL MERGED INTEREST DATA (with UTM from cookies) ===');
    logger.log('Final merged interest data:', JSON.stringify(finalInterestData, null, 2));

    // Parse the name into first and last name
    const nameParts = contactData.Name ? contactData.Name.trim().split(' ') : [];
    const firstName = nameParts[0] || '';
    
    // Add page context to form data
    let pageContextData = '';
    try {
      const { PageContextManager } = await import('./pageContextManager');
      const pageContext = PageContextManager.getLeadContext();
      if (pageContext) {
        pageContextData = `\n\n${pageContext}`;
      }
    } catch (error) {
      console.error('Error adding page context to HTML lead:', error);
    }

    // Extract vehicle information from merged Interest data
    const make = finalInterestData?.vehicle?.make || '';
    const model = finalInterestData?.vehicle?.model || '';
    const version = finalInterestData?.vehicle?.version || '';
    const year = finalInterestData?.vehicle?.year ? String(finalInterestData.vehicle.year) : '';
    const color = finalInterestData?.vehicle?.color || '';
    const vin = finalInterestData?.vehicle?.vin || '';
    const status = finalInterestData?.vehicle?.status || '';
    
    // CRITICAL: Use department from interest data (New/Used) instead of defaulting to Service
    const department = finalInterestData?.department || 'New'; // Default to New for appointments
    const dealerid = finalInterestData?.dealerid || '';
    const utm_source = finalInterestData?.utm_source || '';
    const utm_campaign = finalInterestData?.utm_campaign || '';

    // SECURITY: Contact information no longer stored in Interest data (localStorage)
    // Contact data should only come from form submissions or session manager
    const finalName = contactData.Name || '';
    const finalPhone = contactData.Phone || '';
    const finalEmail = contactData.Email || '';
    const finalFirstName = firstName || '';

    // Extract message from interest data
    const message = finalInterestData?.message || interestData?.message || '';
    
    // Add page context to message
    const finalMessage = message + pageContextData;

    logger.log('=== EXTRACTED FIELD VALUES ===');
    logger.log('Vehicle info:', { make, model, version, year, color, vin, status, department, dealerid, utm_source, utm_campaign });
    logger.log('Contact info:', { finalName, finalPhone, finalEmail, finalFirstName });

    // Prepare the lead data object
    const leadData = {
      "prénom": "", // Leave first name field empty as requested
      "full_name": finalName,
      "email": finalEmail,
      "phone": finalPhone,
      "message": finalMessage, // Include message field with page context
      // Populate vehicle fields from Interest data
      "make": make,
      "model": model,
      "version": version,
      "year": year,
      "color": color,
      "vin": vin,
      "status": status,
      "street": "",
      "address": "",
      "city": "",
      "province": "",
      "postal": "",
      "country": "",
      "departement": department, // This will now be New/Used for appointments based on vehicle interest
      "dealerid": dealerid,
      "utm_source": utm_source,
      "utm_campaign": utm_campaign
    };

    logger.log('=== COMPLETE LEAD DATA OBJECT ===');
    logger.log('Lead data to send:', JSON.stringify(leadData, null, 2));

  // Debug: Count non-empty fields
  const nonEmptyFields = Object.entries(leadData).filter(([key, value]) => value && value !== '').length;
  logger.log(`💰💰💰 NON-EMPTY FIELDS COUNT: ${nonEmptyFields} out of ${Object.keys(leadData).length}`);

  // 🚀 ACTUAL LEAD PUSH TO D2C API
  logger.log('💰💰💰 === PUSHING LEAD TO D2C API ===');
  logger.log('💰💰💰 Lead data:', JSON.stringify(leadData, null, 2));
  
  const startTime = Date.now();

    // TODO: Call D2C API endpoint here
    // For now, just logging that we would push
    logger.log('💰💰💰 ⚠️ WARNING: D2C API call not yet implemented');
    logger.log('💰💰💰 Lead data prepared but API call missing');

    const endTime = Date.now();
    logger.log(`💰💰💰 Request completed in ${endTime - startTime}ms`);
    logger.log('💰💰💰 === LeadPushtoD2C MOCK SUCCESS (API NOT CALLED) ===');
    
    // 🔥 NEW: Inject conversion tracker script after successful webhook call (only once per session)
    const dealerID = await getDealerID();
    if (dealerID && dealerID !== 0) {
      // SECURITY: Validate dealer ID to prevent XSS
      if (typeof dealerID !== 'number' || dealerID <= 0) {
        logger.log('❌ Invalid dealer ID format, skipping conversion tracker script injection');
        return;
      }
      
      // Check if conversion tracker script is already injected
      const dealerIDStr = dealerID.toString();
      const existingConversionScript = document.querySelector(`script[src*="conversiontracker.js"][src*="did=${dealerIDStr}"]`);
      if (existingConversionScript) {
        logger.log('🚫 Conversion tracker script already exists, skipping injection');
      } else {
        const conversionScript = document.createElement('script');
        conversionScript.src = `https://stats.d2cmedia.ca/conversiontracker.js?did=${encodeURIComponent(dealerIDStr)}`;
        conversionScript.async = true;
        document.head.appendChild(conversionScript);
        logger.log('✅ Conversion tracker script injected with dealer ID:', dealerID);
      }
    } else {
      logger.log('⚠️ No valid dealer ID found, conversion tracker script not injected');
    }
    
    // 🔥 Fire Google Ads conversion and lead-sent event
    try {
      const d2cCookie = getD2CChatbotCookie();
      
      // Always fire the lead-sent event
      ga4Tracker.trackLeadSent('chatbot_lead', {
        name: finalName,
        email: finalEmail,
        phone: finalPhone,
        formType: department
      });
      logger.log('✅ D2chatbot-lead-sent event fired');
      
      // Fire Google Ads conversion if Ads object exists in cookie
      if (d2cCookie?.ads?.id && d2cCookie?.ads?.label) {
        ga4Tracker.trackGoogleAdsConversion(d2cCookie.ads.id, d2cCookie.ads.label);
        logger.log('✅ Google Ads conversion fired with ID:', d2cCookie.ads.id, 'Label:', d2cCookie.ads.label);
      } else {
        logger.log('🚫 No Google Ads conversion data in cookie, skipping');
      }
    } catch (adsError) {
      logger.error('❌ Error firing Google Ads conversion:', adsError);
    }
    
    // Log a summary for easy tracking
    
    return true;

  } catch (error) {
    logger.error('=== LeadPushtoD2C ERROR ===');
    logger.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
    
    // Remove from cache on error so it can be retried
    leadPushCache.delete(leadKey);
    return false;
  }
};
